import { useCallback, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { exportToJSON, exportToCSV } from './core';
import { ExportFormat, ExportFileFormat, ExportConfig, ExportableData } from './types';
import { FileDown, Table, FileJson } from 'lucide-react';

interface ExportButtonProps {
  data: ExportableData;
  config: Partial<ExportConfig>;
  format?: ExportFormat;
  className?: string;
  children?: React.ReactNode;
  disabled?: boolean;
  onExportStart?: () => void;
  onExportComplete?: (format: ExportFormat) => void;
  onExportError?: (error: Error) => void;
}

export default function ExportButton({
  data,
  config,
  format = ExportFormat.JSON,
  className = '',
  children,
  disabled = false,
  onExportStart,
  onExportComplete,
  onExportError,
}: ExportButtonProps) {
  const [isExporting, setIsExporting] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleExport = useCallback(
    async (exportFormat: ExportFileFormat) => {
      if (!data || disabled) return;

      setIsExporting(true);
      setIsExpanded(false);
      onExportStart?.();

      const exportConfig: ExportConfig = {
        fileName: config.fileName || 'export',
        format: exportFormat,
        date: config.date ?? true,
        preserveTimestamp: config.preserveTimestamp ?? false,
      };

      try {
        if (exportFormat === ExportFormat.JSON) {
          exportToJSON(data, exportConfig);
        } else {
          exportToCSV(data as ExportableData[], exportConfig);
        }
        onExportComplete?.(exportFormat);
      } catch (error) {
        onExportError?.(error instanceof Error ? error : new Error('Export failed'));
      } finally {
        setIsExporting(false);
      }
    },
    [data, disabled, onExportStart, onExportComplete, onExportError, config],
  );

  const isSingleFormat = format === ExportFormat.JSON || format === ExportFormat.CSV;
  const canExportCSV = Array.isArray(data);

  if (isSingleFormat) {
    const fileFormat = format as ExportFileFormat;
    return (
      <motion.button
        onClick={() => handleExport(fileFormat)}
        disabled={
          disabled || isExporting || (fileFormat === ExportFormat.CSV && !Array.isArray(data))
        }
        className={`rounded-md p-2 transition-colors duration-200 hover:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-300 ${className}`}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        transition={{ type: 'spring', stiffness: 400, damping: 17 }}
        title={children ? children.toString() : `Export ${fileFormat.toUpperCase()}`}
      >
        {isExporting ? (
          <motion.div
            className="h-5 w-5 rounded-full border-2 border-gray-400 border-t-transparent"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          />
        ) : (
          <FileDown className="h-5 w-5" />
        )}
      </motion.button>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <motion.button
        onClick={() => {
          if (isExporting) return;
          setIsExpanded(!isExpanded);
        }}
        disabled={disabled || isExporting}
        className="rounded-md p-2 transition-colors duration-200 disabled:cursor-not-allowed disabled:text-gray-300"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        transition={{ type: 'spring', stiffness: 400, damping: 17 }}
        title={children ? children.toString() : 'Export options'}
      >
        {isExporting ? (
          <motion.div
            className="h-5 w-5 rounded-full border-2 border-gray-400 border-t-transparent"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          />
        ) : (
          <FileDown className="h-5 w-5" />
        )}
      </motion.button>

      <AnimatePresence>
        {isExpanded && !isExporting && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="absolute right-0 top-full z-50 mt-1 min-w-[140px] overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg"
          >
            <motion.button
              onClick={() => handleExport(ExportFormat.JSON)}
              className="flex w-full items-center space-x-2 px-4 py-2 text-left text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-50"
              whileHover={{ backgroundColor: '#f9fafb' }}
              whileTap={{ scale: 0.98 }}
            >
              <FileJson className="h-4 w-4" />
              <span>JSON</span>
            </motion.button>

            <motion.button
              onClick={() => handleExport(ExportFormat.CSV)}
              disabled={!canExportCSV}
              className={`flex w-full items-center space-x-2 px-4 py-2 text-left text-sm transition-colors duration-150 ${
                canExportCSV ? 'text-gray-700 hover:bg-gray-50' : 'cursor-not-allowed text-gray-400'
              }`}
              whileHover={canExportCSV ? { backgroundColor: '#f9fafb' } : {}}
              whileTap={canExportCSV ? { scale: 0.98 } : {}}
            >
              <Table className="h-4 w-4" />
              <span>CSV</span>
              {!canExportCSV && (
                <span className="ml-auto text-xs text-gray-400">(Array required)</span>
              )}
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {isExpanded && <div className="fixed inset-0 z-40" onClick={() => setIsExpanded(false)} />}
    </div>
  );
}
